import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, X, Trash2, Edit2, Eye } from 'lucide-react';
import C from '../../constants/colors';
import {
  createSchedule,
  updateSchedule,
  deleteSchedule,
  listSessions,
  getOverview,
  listOverrides,
  reviewOverride,
  reviewSelfie,
} from '../../api/timeAttendance';

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString() + ' ' + fmtTime(iso);
}

function GeofenceBadge({ status }) {
  const map = {
    WITHIN:           { label: 'Within',   color: C.green },
    OUTSIDE_OVERRIDE: { label: 'Override', color: C.amber },
    OUTSIDE_BLOCKED:  { label: 'Blocked',  color: C.red },
    PENDING_OVERRIDE: { label: 'Pending',  color: C.blue },
    OUTSIDE_PENDING:  { label: 'Pending',  color: C.blue },
  };
  const { label, color } = map[status] || { label: status, color: C.textMuted };
  return (
    <span style={{
      fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
      background: color + '22', color,
      border: `1px solid ${color}44`, fontWeight: 600,
    }}>
      {label}
    </span>
  );
}

function SelfieBadge({ status }) {
  if (!status || status === 'MATCH') return null;
  const map = {
    NO_MATCH: { label: 'ID Mismatch', color: C.amber },
    SKIPPED:  { label: 'No Photo',    color: C.textMuted },
    ERROR:    { label: 'Check Failed', color: C.textMuted },
  };
  const { label, color } = map[status] || { label: status, color: C.textMuted };
  return (
    <span style={{
      fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
      background: color + '22', color,
      border: `1px solid ${color}44`, fontWeight: 600,
    }}>
      {label}
    </span>
  );
}

// ─── ScheduleFormModal ───────────────────────────────────────────────────────

function ScheduleFormModal({ schedule, companies, sites, onSave, onClose }) {
  const isNew = !schedule?.id;
  const [form, setForm] = useState({
    companyId:     schedule?.companyId     || '',
    siteId:        schedule?.siteId        || '',
    name:          schedule?.name          || '',
    type:          schedule?.type          || 'STANDARD',
    workDays:      schedule?.workDays      || [1, 2, 3, 4, 5],
    dailyStart:    schedule?.dailyStart    || '08:00',
    dailyEnd:      schedule?.dailyEnd      || '17:00',
    daysOn:        schedule?.daysOn        || 14,
    daysOff:       schedule?.daysOff       || 14,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filteredSites = sites.filter(s => !form.companyId || s.companyId === form.companyId);

  function setField(key, val) {
    setForm(prev => ({ ...prev, [key]: val }));
  }

  async function handleSave() {
    if (!form.companyId || !form.name.trim()) {
      setError('Company and name are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        companyId: form.companyId,
        siteId:    form.siteId || null,
        name:      form.name.trim(),
        type:      form.type,
        ...(form.type === 'STANDARD' ? {
          workDays:   form.workDays,
          dailyStart: form.dailyStart,
          dailyEnd:   form.dailyEnd,
        } : {}),
        ...(form.type === 'ROTATION' ? {
          daysOn:        parseInt(form.daysOn),
          daysOff:       parseInt(form.daysOff),
        } : {}),
      };
      const result = isNew
        ? await createSchedule(body)
        : await updateSchedule(schedule.id, body);
      onSave(isNew ? 'created' : 'updated', result);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px',
    color: C.textPrimary, fontSize: '13px', padding: '8px 10px',
    outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle = { fontSize: '11px', color: C.textMuted, marginBottom: '4px', display: 'block' };
  const fieldStyle = { marginBottom: '14px' };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: '16px',
        padding: '24px', width: '480px', maxWidth: '95vw', maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: C.textPrimary }}>
            {isNew ? 'Add Schedule' : 'Edit Schedule'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>
            <X size={18} />
          </button>
        </div>

        {/* Company */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Company *</label>
          <select value={form.companyId} onChange={e => setField('companyId', e.target.value)} style={inputStyle}>
            <option value="">— select —</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* Site */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Site (optional)</label>
          <select value={form.siteId} onChange={e => setField('siteId', e.target.value)} style={inputStyle}>
            <option value="">— all sites —</option>
            {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Name */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Schedule Name *</label>
          <input
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            placeholder="e.g. Day Shift"
            style={inputStyle}
          />
        </div>

        {/* Type */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Type</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {['STANDARD', 'ROTATION'].map(t => (
              <button
                key={t}
                onClick={() => setField('type', t)}
                style={{
                  padding: '6px 16px', borderRadius: '8px', border: `1px solid ${form.type === t ? C.blue : C.border}`,
                  background: form.type === t ? C.blue + '22' : 'transparent',
                  color: form.type === t ? C.blue : C.textSecondary,
                  cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit',
                }}
              >
                {t.charAt(0) + t.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        {/* STANDARD fields */}
        {form.type === 'STANDARD' && (
          <>
            <div style={fieldStyle}>
              <label style={labelStyle}>Work Days</label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => {
                  const val = i + 1;
                  const checked = form.workDays.includes(val);
                  return (
                    <label
                      key={val}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: checked ? C.textPrimary : C.textMuted, cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setField('workDays', checked
                          ? form.workDays.filter(x => x !== val)
                          : [...form.workDays, val].sort((a, b) => a - b)
                        )}
                      />
                      {d}
                    </label>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Start Time</label>
                <input type="time" value={form.dailyStart} onChange={e => setField('dailyStart', e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>End Time</label>
                <input type="time" value={form.dailyEnd} onChange={e => setField('dailyEnd', e.target.value)} style={inputStyle} />
              </div>
            </div>
          </>
        )}

        {/* ROTATION fields */}
        {form.type === 'ROTATION' && (
          <>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Days On</label>
                <input type="number" min="1" value={form.daysOn} onChange={e => setField('daysOn', e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Days Off</label>
                <input type="number" min="1" value={form.daysOff} onChange={e => setField('daysOff', e.target.value)} style={inputStyle} />
              </div>
            </div>
          </>
        )}

        {error && (
          <div style={{ marginBottom: '14px', fontSize: '12px', color: C.red }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 18px', borderRadius: '8px', border: `1px solid ${C.border}`,
              background: 'transparent', color: C.textSecondary, cursor: 'pointer',
              fontSize: '13px', fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 18px', borderRadius: '8px', border: 'none',
              background: C.blue, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: 600, fontFamily: 'inherit',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ReviewModal ─────────────────────────────────────────────────────────────

function ReviewModal({ override, onReview, onClose }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isReviewed = override.status === 'APPROVED' || override.status === 'REJECTED';

  async function submit(status) {
    if (!reason.trim()) { setError('Review reason is required.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await reviewOverride(override.id, { status, reviewReason: reason.trim() });
      onReview(override.id, status, reason.trim());
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Review failed.');
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle = {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px',
    color: C.textPrimary, fontSize: '13px', padding: '8px 10px',
    outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
    resize: 'vertical', minHeight: '80px',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: '16px',
        padding: '24px', width: '420px', maxWidth: '95vw',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: C.textPrimary }}>
            {isReviewed ? 'Override History' : 'Review Override'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ marginBottom: '16px', fontSize: '13px', color: C.textSecondary, lineHeight: '1.6' }}>
          <div><span style={{ color: C.textMuted }}>Person: </span><span style={{ color: C.textPrimary, fontWeight: 500 }}>{override.personName || override.person_name || override.personId}</span></div>
          <div><span style={{ color: C.textMuted }}>Site: </span>{override.siteName || override.site_name || '—'}</div>
          <div><span style={{ color: C.textMuted }}>Clock-in: </span>{fmtDateTime(override.clockInAt || override.clock_in_at)}</div>
          <div><span style={{ color: C.textMuted }}>Distance: </span>{(override.distanceM ?? override.distance_m) != null ? `${override.distanceM ?? override.distance_m} m from site` : '—'}</div>
          <div><span style={{ color: C.textMuted }}>Employee reason: </span>{override.reason || '—'}</div>
        </div>

        {isReviewed ? (
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontSize: '12px', fontWeight: 600, padding: '5px 12px', borderRadius: '8px', marginBottom: '12px',
              background: (override.status === 'APPROVED' ? C.green : C.red) + '22',
              color: override.status === 'APPROVED' ? C.green : C.red,
              border: `1px solid ${(override.status === 'APPROVED' ? C.green : C.red)}44`,
            }}>
              {override.status === 'APPROVED' ? '✓ Approved' : '✗ Rejected'}
            </div>
            {(override.reviewReason || override.review_reason) && (
              <div style={{ fontSize: '13px', color: C.textSecondary, marginBottom: '8px', lineHeight: '1.5' }}>
                <span style={{ color: C.textMuted }}>Review reason: </span>
                {override.reviewReason || override.review_reason}
              </div>
            )}
            {(override.reviewedAt || override.reviewed_at) && (
              <div style={{ fontSize: '12px', color: C.textMuted }}>
                Reviewed: {fmtDateTime(override.reviewedAt || override.reviewed_at)}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' }}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '11px', color: C.textMuted, marginBottom: '4px', display: 'block' }}>
                Review Reason *
              </label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Enter your review reason…"
                style={inputStyle}
              />
            </div>
            {error && <div style={{ marginBottom: '12px', fontSize: '12px', color: C.red }}>{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={onClose}
                style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' }}
              >
                Cancel
              </button>
              <button
                onClick={() => submit('REJECTED')}
                disabled={submitting}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: C.red, color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', opacity: submitting ? 0.7 : 1 }}
              >
                Reject
              </button>
              <button
                onClick={() => submit('APPROVED')}
                disabled={submitting}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: C.green, color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', opacity: submitting ? 0.7 : 1 }}
              >
                Approve
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SelfieReviewModal ───────────────────────────────────────────────────────

function SelfieReviewModal({ session, onClose, onReviewed }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const profilePhoto = session.profilePhotoUrl || session.profile_photo_url;
  const selfieUrl    = session.selfieUrl        || session.selfie_url;

  async function handleApprove() {
    if (!note.trim()) { setError('Please enter a reason before approving.'); return; }
    setSaving(true);
    try {
      await reviewSelfie(session.id, note.trim());
      onReviewed(session.id, note.trim());
      onClose();
    } catch {
      setError('Failed to save review. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: C.surface, borderRadius: '14px', border: `1px solid ${C.border}`,
        width: '560px', maxWidth: '95vw', padding: '24px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: C.textPrimary }}>Selfie Review</div>
            <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '2px' }}>
              {session.personName || session.person_name} &mdash; {new Date(session.clockInAt || session.clock_in_at).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
            <X size={18} />
          </button>
        </div>

        {/* Photos */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Profile Photo</div>
            {profilePhoto
              ? <img src={profilePhoto} alt="Profile" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '10px', border: `1px solid ${C.border}` }} />
              : <div style={{ width: '100%', aspectRatio: '1', background: C.surfaceElevated, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, fontSize: '12px' }}>No photo</div>
            }
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Clock-In Selfie</div>
            {selfieUrl
              ? <img src={selfieUrl} alt="Selfie" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '10px', border: `1px solid ${C.border}` }} />
              : <div style={{ width: '100%', aspectRatio: '1', background: C.surfaceElevated, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, fontSize: '12px' }}>No selfie</div>
            }
          </div>
        </div>

        {/* Reason */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '12px', color: C.textMuted, display: 'block', marginBottom: '6px' }}>Reason for approval</label>
          <textarea
            value={note}
            onChange={e => { setNote(e.target.value); setError(null); }}
            placeholder="e.g. Confirmed same person, lighting difference caused mismatch"
            rows={3}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px',
              background: C.surfaceElevated, border: `1px solid ${error ? C.red : C.border}`,
              borderRadius: '8px', color: C.textPrimary, fontSize: '13px',
              resize: 'vertical', fontFamily: 'inherit',
            }}
          />
          {error && <div style={{ fontSize: '12px', color: C.red, marginTop: '4px' }}>{error}</div>}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '9px 18px', borderRadius: '8px', border: `1px solid ${C.border}`,
            background: 'transparent', color: C.textMuted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit',
          }}>Cancel</button>
          <button onClick={handleApprove} disabled={saving} style={{
            padding: '9px 18px', borderRadius: '8px', border: 'none',
            background: C.green, color: '#fff', fontSize: '13px', fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit',
          }}>
            {saving ? 'Saving…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main View ───────────────────────────────────────────────────────────────

export default function TimeAttendanceView({
  companyId,
  companies,
  sites,
  employees,
  schedules,
  onScheduleCreated,
  onScheduleUpdated,
  onScheduleDeleted,
}) {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);
  const [selfieReviewSession, setSelfieReviewSession] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  const company = companies.find(c => c.id === companyId);

  // Load data when component mounts (companyId is fixed via key prop in App.jsx)
  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      getOverview(companyId),
      listSessions({ companyId }),
      listOverrides({ companyId, status: 'PENDING' }),
      listOverrides({ companyId, status: 'APPROVED' }),
      listOverrides({ companyId, status: 'REJECTED' }),
    ])
      .then(([ov, sess, pending, approved, rejected]) => {
        setOverview(ov);
        setSessions(sess);
        setOverrides([...pending, ...approved, ...rejected]);
      })
      .finally(() => setLoading(false));
  }, [companyId]);

  const companySchedules = schedules.filter(s => s.companyId === companyId);

  async function handleDeleteSchedule(id) {
    if (!window.confirm('Delete this schedule?')) return;
    await deleteSchedule(id);
    onScheduleDeleted(id);
  }

  function getSiteName(siteId) {
    const s = sites.find(x => x.id === siteId);
    return s ? s.name : siteId || '—';
  }

  function handleSelfieReviewed(sessionId, note) {
    setSessions(prev => prev.map(s =>
      s.id === sessionId ? { ...s, selfieReviewNote: note, selfie_review_note: note } : s
    ));
  }

  // ── Tabs bar ───────────────────────────────────────────────────────────────

  const TABS = ['overview', 'records', 'schedules', 'overrides'];

  // ── Shared table styles ────────────────────────────────────────────────────

  const rowStyle = {
    display: 'flex', alignItems: 'center',
    padding: '10px 14px', borderBottom: `1px solid ${C.border}`,
    fontSize: '13px', color: C.textPrimary, gap: '12px',
  };
  const headerRowStyle = {
    ...rowStyle,
    fontSize: '11px', color: C.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.05em',
    borderBottom: `1px solid ${C.border}`,
    background: C.surfaceElevated,
    borderRadius: '8px 8px 0 0',
  };

  // ── Render tab content ─────────────────────────────────────────────────────

  function renderOverview() {
    if (loading || !overview) {
      return (
        <div style={{ color: C.textMuted, fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>
          {loading ? 'Loading…' : 'No data available.'}
        </div>
      );
    }

    const stats = [
      { label: 'Clocked In',       value: overview.clockedIn       ?? 0, color: C.blue },
      { label: 'Clocked Out Today', value: overview.clockedOut      ?? 0, color: C.green },
      { label: 'Pending Overrides', value: overview.pendingOverrides ?? 0, color: C.amber },
    ];

    const liveList = overview.liveList || [];

    return (
      <>
        {/* Stat cards */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {stats.map(stat => (
            <div key={stat.label} style={{
              background: C.surfaceElevated, border: `1px solid ${C.border}`,
              borderRadius: '12px', padding: '16px 20px', flex: '1', minWidth: '120px',
            }}>
              <div style={{ fontSize: '11px', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                {stat.label}
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: stat.color }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Currently On Site */}
        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>
          Currently On Site
        </div>
        {liveList.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: '13px', padding: '20px 0' }}>
            No one currently clocked in.
          </div>
        ) : (
          <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
            <div style={headerRowStyle}>
              <span style={{ flex: 2 }}>Name</span>
              <span style={{ flex: 1 }}>Code</span>
              <span style={{ flex: 1 }}>Clocked In</span>
              <span style={{ flex: 1 }}>Geofence</span>
            </div>
            {liveList.map((p, i) => (
              <div key={i} style={rowStyle}>
                <span style={{ flex: 2 }}>{p.personName || p.person_name || p.personId}</span>
                <span style={{ flex: 1, color: C.textSecondary }}>{p.personCode || p.person_code || '—'}</span>
                <span style={{ flex: 1, color: C.textSecondary }}>{fmtTime(p.clockInAt || p.clock_in_at)}</span>
                <span style={{ flex: 1 }}>
                  <GeofenceBadge status={p.geofenceStatus || p.geofence_status} />
                </span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  function renderRecords() {
    if (loading) {
      return <div style={{ color: C.textMuted, fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>Loading…</div>;
    }
    if (sessions.length === 0) {
      return <div style={{ color: C.textMuted, fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>No records found.</div>;
    }

    // Group by person + date
    const groupMap = {};
    sessions.forEach(s => {
      const personId  = s.personId  || s.person_id;
      const clockInAt = s.clockInAt || s.clock_in_at;
      const date      = clockInAt ? new Date(clockInAt).toLocaleDateString() : 'Unknown';
      const key       = `${personId}__${date}`;
      if (!groupMap[key]) groupMap[key] = { key, personName: s.personName || s.person_name || personId, date, sessions: [] };
      groupMap[key].sessions.push(s);
    });
    const groups = Object.values(groupMap);

    function renderSessionRow(s, i, isLast) {
      const hoursWorked    = s.hoursWorked ?? s.hours_worked;
      const statusVal      = s.status;
      const statusColor    = statusVal === 'IN_PROGRESS' ? C.blue : C.green;
      const statusLabel    = statusVal === 'IN_PROGRESS' ? 'In Progress' : 'Completed';
      const selfieStatus   = s.selfieStatus || s.selfie_status;
      const reviewNote     = s.selfieReviewNote || s.selfie_review_note;
      const isNoMatch      = selfieStatus === 'NO_MATCH';
      const emp            = employees.find(e => e.id === (s.personId || s.person_id));
      const profilePhoto   = emp?.photoUrl || emp?.photo_url;
      const geofenceStatus = s.geofenceStatus || s.geofence_status;
      const overrideForSession = ['OUTSIDE_PENDING', 'OUTSIDE_OVERRIDE', 'OUTSIDE_BLOCKED'].includes(geofenceStatus)
        ? overrides.find(o => (o.sessionId || o.session_id) === s.id)
        : null;
      const geoBadgeMap = {
        WITHIN:           { label: 'Within',   color: C.green },
        OUTSIDE_OVERRIDE: { label: 'Override', color: C.amber },
        OUTSIDE_BLOCKED:  { label: 'Blocked',  color: C.red },
        OUTSIDE_PENDING:  { label: 'Pending',  color: C.blue },
      };
      const geoBadge = geoBadgeMap[geofenceStatus] || { label: geofenceStatus, color: C.textMuted };
      return (
        <div key={s.id || i} style={{
          ...rowStyle,
          paddingLeft: '32px',
          background: C.surface,
          borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
          fontSize: '12px',
        }}>
          <span style={{ flex: 2, color: C.textMuted }}>{fmtDateTime(s.clockInAt || s.clock_in_at)}</span>
          <span style={{ flex: 2, color: C.textMuted }}>
            {(s.clockOutAt || s.clock_out_at) ? fmtDateTime(s.clockOutAt || s.clock_out_at) : <span style={{ fontStyle: 'italic' }}>In progress</span>}
          </span>
          <span style={{ flex: 1, color: C.textMuted }}>
            {hoursWorked != null ? Number(hoursWorked).toFixed(1) + 'h' : '—'}
          </span>
          <span style={{ flex: 1 }}>
            {overrideForSession ? (
              <span
                onClick={() => setReviewModal(overrideForSession)}
                title="Click to view override details"
                style={{
                  fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
                  background: geoBadge.color + '22', color: geoBadge.color,
                  border: `1px solid ${geoBadge.color}44`, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {geoBadge.label} ›
              </span>
            ) : (
              <GeofenceBadge status={geofenceStatus} />
            )}
          </span>
          <span style={{ flex: 1 }}>
            {isNoMatch && !reviewNote ? (
              <span
                onClick={() => setSelfieReviewSession({ ...s, profilePhotoUrl: profilePhoto })}
                title="Click to review"
                style={{
                  fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
                  background: C.amber + '22', color: C.amber,
                  border: `1px solid ${C.amber}44`, fontWeight: 600, cursor: 'pointer',
                }}
              >ID Mismatch</span>
            ) : isNoMatch && reviewNote ? (
              <span title={reviewNote} style={{
                fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
                background: C.textMuted + '22', color: C.textMuted,
                border: `1px solid ${C.textMuted}44`, fontWeight: 600,
              }}>Reviewed</span>
            ) : (
              <SelfieBadge status={selfieStatus} />
            )}
          </span>
          <span style={{ flex: 1 }}>
            <span style={{
              fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
              background: statusColor + '22', color: statusColor,
              border: `1px solid ${statusColor}44`, fontWeight: 600,
            }}>{statusLabel}</span>
          </span>
        </div>
      );
    }

    return (
      <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ ...headerRowStyle, fontSize: '11px' }}>
          <span style={{ flex: 3 }}>Person</span>
          <span style={{ flex: 2 }}>Date</span>
          <span style={{ flex: 1 }}>Total Hours</span>
          <span style={{ flex: 1 }}>Sessions</span>
          <span style={{ flex: 1 }}></span>
        </div>

        {/* Group rows */}
        {groups.map((g, gi) => {
          const isExpanded   = !!expandedGroups[g.key];
          const totalHours   = g.sessions.reduce((sum, s) => sum + parseFloat(s.hoursWorked ?? s.hours_worked ?? 0), 0);
          const hasInProgress = g.sessions.some(s => s.status === 'IN_PROGRESS');
          const hasMismatch  = g.sessions.some(s => (s.selfieStatus || s.selfie_status) === 'NO_MATCH' && !(s.selfieReviewNote || s.selfie_review_note));
          const isLastGroup  = gi === groups.length - 1;

          return (
            <div key={g.key}>
              {/* Summary row */}
              <div
                onClick={() => setExpandedGroups(prev => ({ ...prev, [g.key]: !prev[g.key] }))}
                style={{
                  ...rowStyle,
                  cursor: 'pointer',
                  borderBottom: (isExpanded || !isLastGroup) ? `1px solid ${C.border}` : 'none',
                  background: isExpanded ? C.surface : 'transparent',
                  userSelect: 'none',
                }}
              >
                <span style={{ flex: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {g.personName}
                  {hasMismatch && (
                    <span style={{
                      fontSize: '10px', padding: '1px 6px', borderRadius: '5px',
                      background: C.amber + '22', color: C.amber,
                      border: `1px solid ${C.amber}44`, fontWeight: 600,
                    }}>ID Mismatch</span>
                  )}
                </span>
                <span style={{ flex: 2, color: C.textSecondary }}>{g.date}</span>
                <span style={{ flex: 1, color: hasInProgress ? C.blue : C.textPrimary, fontWeight: 600 }}>
                  {totalHours > 0 ? totalHours.toFixed(1) + 'h' : '—'}
                  {hasInProgress && <span style={{ color: C.blue, fontSize: '11px', marginLeft: '4px' }}>live</span>}
                </span>
                <span style={{ flex: 1, color: C.textMuted }}>{g.sessions.length} session{g.sessions.length !== 1 ? 's' : ''}</span>
                <span style={{ flex: 1, color: C.textMuted, fontSize: '16px', textAlign: 'right', paddingRight: '8px' }}>
                  {isExpanded ? '−' : '+'}
                </span>
              </div>

              {/* Expanded detail rows */}
              {isExpanded && (
                <div>
                  <div style={{ ...headerRowStyle, fontSize: '10px', paddingLeft: '32px', borderRadius: 0, background: C.surfaceElevated }}>
                    <span style={{ flex: 2 }}>Clock In</span>
                    <span style={{ flex: 2 }}>Clock Out</span>
                    <span style={{ flex: 1 }}>Hours</span>
                    <span style={{ flex: 1 }}>Geofence</span>
                    <span style={{ flex: 1 }}>Selfie</span>
                    <span style={{ flex: 1 }}>Status</span>
                  </div>
                  {g.sessions.map((s, i) => renderSessionRow(s, i, i === g.sessions.length - 1))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function renderSchedules() {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button
            onClick={() => setScheduleModal('create')}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: 'none',
              background: C.blue, color: '#fff', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            + Add Schedule
          </button>
        </div>

        {companySchedules.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>
            No schedules configured for this company.
          </div>
        ) : (
          <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
            <div style={headerRowStyle}>
              <span style={{ flex: 2 }}>Name</span>
              <span style={{ flex: 1 }}>Type</span>
              <span style={{ flex: 2 }}>Site</span>
              <span style={{ flex: 3 }}>Details</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Actions</span>
            </div>
            {companySchedules.map(s => {
              const detailsText = s.type === 'STANDARD'
                ? `${(s.workDays || []).map(d => ['','Mon','Tue','Wed','Thu','Fri','Sat','Sun'][d]).join(', ')} · ${s.dailyStart || ''}–${s.dailyEnd || ''}`
                : `${s.daysOn ?? '?'} on / ${s.daysOff ?? '?'} off`;
              return (
                <div key={s.id} style={rowStyle}>
                  <span style={{ flex: 2, fontWeight: 500 }}>{s.name}</span>
                  <span style={{ flex: 1 }}>
                    <span style={{
                      fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
                      background: s.type === 'ROTATION' ? C.purple + '22' : C.blue + '22',
                      color: s.type === 'ROTATION' ? C.purple : C.blue,
                      border: `1px solid ${s.type === 'ROTATION' ? C.purple : C.blue}44`,
                      fontWeight: 600,
                    }}>
                      {s.type === 'ROTATION' ? 'Rotation' : 'Standard'}
                    </span>
                  </span>
                  <span style={{ flex: 2, color: C.textSecondary }}>{getSiteName(s.siteId)}</span>
                  <span style={{ flex: 3, color: C.textMuted, fontSize: '12px' }}>{detailsText}</span>
                  <span style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                    <button
                      onClick={() => setScheduleModal(s)}
                      title="Edit"
                      style={{
                        background: 'none', border: `1px solid ${C.border}`, borderRadius: '6px',
                        color: C.textSecondary, cursor: 'pointer', padding: '4px 6px',
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      <Edit2 size={13} />
                    </button>
                    <button
                      onClick={() => handleDeleteSchedule(s.id)}
                      title="Delete"
                      style={{
                        background: 'none', border: `1px solid ${C.border}`, borderRadius: '6px',
                        color: C.red, cursor: 'pointer', padding: '4px 6px',
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  function renderOverrides() {
    if (loading) {
      return <div style={{ color: C.textMuted, fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>Loading…</div>;
    }
    if (overrides.length === 0) {
      return <div style={{ color: C.textMuted, fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>No overrides found.</div>;
    }

    return (
      <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
        <div style={headerRowStyle}>
          <span style={{ flex: 2 }}>Person</span>
          <span style={{ flex: 2 }}>Site</span>
          <span style={{ flex: 2 }}>Clock In</span>
          <span style={{ flex: 2 }}>Reason</span>
          <span style={{ flex: 1 }}>Distance</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Action</span>
        </div>
        {overrides.map((o, i) => (
          <div key={o.id || i} style={rowStyle}>
            <span style={{ flex: 2, fontWeight: 500 }}>{o.personName || o.person_name || o.personId}</span>
            <span style={{ flex: 2, color: C.textSecondary }}>{o.siteName || o.site_name || '—'}</span>
            <span style={{ flex: 2, color: C.textSecondary }}>{fmtDateTime(o.clockInAt || o.clock_in_at)}</span>
            <span style={{ flex: 2, color: C.textMuted, fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {o.reason || '—'}
            </span>
            <span style={{ flex: 1, color: C.amber, fontSize: '12px' }}>
              {(o.distanceM ?? o.distance_m) != null ? `${o.distanceM ?? o.distance_m} m` : '—'}
            </span>
            <span style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setReviewModal(o)}
                style={{
                  padding: '5px 10px', borderRadius: '6px', border: `1px solid ${C.blue}44`,
                  background: C.blue + '22', color: C.blue,
                  cursor: 'pointer', fontSize: '11px', fontWeight: 600, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}
              >
                <Eye size={11} /> Review
              </button>
            </span>
          </div>
        ))}
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <Clock size={20} color={C.blue} />
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: C.textPrimary }}>Time &amp; Attendance</h2>
          {company && (
            <span style={{ fontSize: '13px', color: C.textMuted, fontWeight: 400 }}>— {company.name}</span>
          )}
        </div>

        {/* Tabs */}
        {companyId && (
          <div style={{ display: 'flex', gap: '4px' }}>
            {TABS.map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  background: tab === t ? C.blue : 'transparent',
                  color: tab === t ? '#fff' : C.textSecondary,
                  fontWeight: tab === t ? 600 : 400,
                  fontSize: '13px', fontFamily: 'inherit',
                }}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
                {t === 'overrides' && overrides.filter(o => o.status === 'PENDING').length > 0 && (
                  <span style={{
                    marginLeft: '6px', background: C.red, color: '#fff',
                    borderRadius: '10px', padding: '1px 7px', fontSize: '11px',
                  }}>
                    {overrides.filter(o => o.status === 'PENDING').length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        <>
          {tab === 'overview'   && renderOverview()}
          {tab === 'records'    && renderRecords()}
          {tab === 'schedules'  && renderSchedules()}
          {tab === 'overrides'  && renderOverrides()}
        </>
      </div>

      {/* Modals */}
      {scheduleModal !== null && (
        <ScheduleFormModal
          schedule={scheduleModal === 'create' ? null : scheduleModal}
          companies={company ? [company] : []}
          sites={sites}
          onSave={(action, result) => {
            if (action === 'created') onScheduleCreated(result);
            else onScheduleUpdated(result);
          }}
          onClose={() => setScheduleModal(null)}
        />
      )}

      {reviewModal !== null && (
        <ReviewModal
          override={reviewModal}
          onReview={(id, newStatus, reviewReason) => {
            setOverrides(prev => prev.map(o =>
              o.id === id ? { ...o, status: newStatus, reviewReason, reviewedAt: new Date().toISOString() } : o
            ));
            if (companyId) {
              getOverview(companyId).then(ov => setOverview(ov)).catch(() => {});
            }
          }}
          onClose={() => setReviewModal(null)}
        />
      )}

      {selfieReviewSession !== null && (
        <SelfieReviewModal
          session={selfieReviewSession}
          onReviewed={handleSelfieReviewed}
          onClose={() => setSelfieReviewSession(null)}
        />
      )}
    </div>
  );
}
